import React from "react";
import PropTypes from "prop-types";
import Loader from "shared/Loader";
import AlertNotice from "shared/AlertNotice";
import Pagination from "shared/Pagination";
import RadioButton from "shared/@forms/RadioButton";
import { promotions as promotionsApi, errorMessage } from "shared/api";
import { formatShortDate } from "shared/@utils/formatDate";
import FilterSidebar from "../components/FilterSidebar";
import PromotionItemCard from "./PromotionItemCard";
// Reuse the Store catalog's search row / filter layout / sort-modal styling so
// Promotions matches the product list, but render rows with our own 4-column
// PromotionItemCard (Image / Title / Type / Status) + a matching header.
import ClearSearchButton from "shared/@forms/ClearSearchButton";
import styles from "../Store/components/ProductCatalog/ProductCatalogBrowser.module.css";
import listStyles from "./PromotionItemCard.module.css";

const STATUS_LABELS = {
  archived: "Archived",
  in_review: "In Review",
  scheduled: "Scheduled",
  published: "Published",
  draft: "Draft",
};

const PROMO_FILTER_GROUPS = [
  {
    title: "Filter by Status",
    key: "status",
    options: ["Published", "Scheduled", "In Review", "Archived"],
  },
  { title: "Filter by Type", key: "type", options: [] },
];

// Map a promotion to the shape PromotionItemCard renders.
const toListItem = (p) => ({
  id: p.id,
  image: p.image || null,
  name: p.name,
  date: formatShortDate(p.updatedAt),
  summary: p.summary,
  type: p.type,
  status: p.status,
  statusLabel: STATUS_LABELS[p.status] || p.status,
});

/**
 * Promotions catalog browser. A trimmed sibling of the Store's
 * ProductCatalogBrowser that reuses the same row card, filter sidebar,
 * pagination and styles but talks to the (mock) promotions API and only needs
 * Status / Type filters and Date / Name / Status sorting.
 */
export default function PromotionsCatalog({
  searchPlaceholder,
  initialFilters,
  toolbarActions,
  renderItemActions,
  actionColWidth,
  refreshKey,
}) {
  const [searchTerm, setSearchTerm] = React.useState("");
  const [searchDraft, setSearchDraft] = React.useState("");
  const searchTimerRef = React.useRef(null);
  const [selectedFilters, setSelectedFilters] = React.useState(initialFilters);
  const [sortField, setSortField] = React.useState("updated"); // updated | name | status
  const [sortOrder, setSortOrder] = React.useState("desc");
  const [sortMenuOpen, setSortMenuOpen] = React.useState(false);
  const [typeOptions, setTypeOptions] = React.useState([]);
  const [promotions, setPromotions] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [page, setPage] = React.useState(1);
  const [total, setTotal] = React.useState(0);
  // 10 matches the shared Pagination component's page-size options — an
  // off-list initial value (e.g. 8) renders as a blank/incorrect select.
  const [pageSize, setPageSize] = React.useState(10);
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const reqIdRef = React.useRef(0);

  const sort = `${sortField}_${sortOrder}`;

  const orderLabels =
    sortField === "updated"
      ? { asc: "Oldest first", desc: "Newest first" }
      : sortField === "status"
      ? { asc: "Status: A to Z", desc: "Status: Z to A" }
      : { asc: "A to Z", desc: "Z to A" };

  React.useEffect(() => {
    let cancelled = false;
    promotionsApi
      .listTypes()
      .then((types) => {
        if (!cancelled) setTypeOptions(Array.isArray(types) ? types : []);
      })
      .catch(() => {
        if (!cancelled) setTypeOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [refreshKey]);

  const dynamicFilterGroups = React.useMemo(
    () =>
      PROMO_FILTER_GROUPS.map((g) =>
        g.key === "type" ? { ...g, options: typeOptions } : g
      ),
    [typeOptions]
  );

  React.useEffect(() => {
    setPage(1);
  }, [searchTerm, selectedFilters, sort, pageSize]);

  React.useEffect(() => {
    const reqId = ++reqIdRef.current;
    setLoading(true);
    setError(null);
    promotionsApi
      .list({
        search: searchTerm,
        filters: selectedFilters,
        sort,
        page,
        pageSize,
      })
      .then((res) => {
        if (reqId !== reqIdRef.current) return;
        const items = Array.isArray(res) ? res : res?.items || [];
        setPromotions(items);
        setTotal(
          Array.isArray(res) ? items.length : res?.total ?? items.length
        );
        setLoading(false);
      })
      .catch((err) => {
        if (reqId !== reqIdRef.current) return;
        setError(errorMessage(err, "Couldn't load promotions."));
        setLoading(false);
      });
  }, [searchTerm, selectedFilters, sort, page, pageSize, refreshKey]);

  const handleSearchChange = React.useCallback((event) => {
    const value = event.target.value;
    setSearchDraft(value);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => setSearchTerm(value), 300);
  }, []);

  const clearSearch = React.useCallback(() => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    setSearchDraft("");
    setSearchTerm("");
  }, []);

  React.useEffect(
    () => () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    },
    []
  );

  const handleFilterChange = React.useCallback((key, option, checked) => {
    setSelectedFilters((prev) => {
      const currentValues = prev[key] || [];
      const nextValues = checked
        ? currentValues.includes(option)
          ? currentValues
          : [...currentValues, option]
        : currentValues.filter((v) => v !== option);
      return { ...prev, [key]: nextValues };
    });
  }, []);

  const handleClearGroup = React.useCallback((key) => {
    setSelectedFilters((prev) => ({ ...prev, [key]: [] }));
  }, []);

  const handleResetFilters = React.useCallback(() => {
    setSelectedFilters(initialFilters);
  }, [initialFilters]);

  const goToPage = React.useCallback(
    (next) => {
      const clamped = Math.min(Math.max(1, next), totalPages);
      if (clamped !== page) {
        setPage(clamped);
        const el = document.getElementById("scrollableDiv");
        if (el) el.scrollTop = 0;
        else window.scrollTo(0, 0);
      }
    },
    [page, totalPages]
  );

  const listItems = React.useMemo(
    () => promotions.map(toListItem),
    [promotions]
  );
  const resultsLabel = `${total} ${total === 1 ? "result" : "results"}`;

  return (
    <div className={styles.browser}>
      <div className={styles.searchRow}>
        <div className={styles.searchInput}>
          <input
            type="search"
            placeholder={searchPlaceholder}
            aria-label={searchPlaceholder}
            value={searchDraft}
            onChange={handleSearchChange}
            onKeyDown={(event) => {
              if (event.key === "Escape") clearSearch();
            }}
          />
          {searchDraft ? <ClearSearchButton onClick={clearSearch} /> : null}
        </div>
        {toolbarActions}
        <button
          type="button"
          className={styles.sortButton}
          title="Change the sort order of the promotions list."
          onClick={() => setSortMenuOpen(true)}
        >
          Sort
        </button>
      </div>

      <div className={styles.catalogLayout}>
        <FilterSidebar
          groups={dynamicFilterGroups}
          selectedFilters={selectedFilters}
          onChange={handleFilterChange}
          onClearGroup={handleClearGroup}
          onResetAll={handleResetFilters}
        />

        <div className={styles.productsColumn}>
          {error && (
            <div className={styles.alertWrap}>
              <AlertNotice title="Couldn't load promotions">
                {error}
              </AlertNotice>
            </div>
          )}

          {loading ? (
            <div className={styles.loaderWrap}>
              <Loader size={60} />
            </div>
          ) : (
            <>
              <div className={styles.list}>
                <header
                  className={listStyles.listHeader}
                  style={{ "--action-col-width": actionColWidth }}
                >
                  <span>Image</span>
                  <span>Title/Info</span>
                  <span>Type:</span>
                  <span>Status:</span>
                  <span className={listStyles.resultsCount}>
                    {resultsLabel}
                  </span>
                </header>

                {!listItems.length ? (
                  <div className={styles.emptyState}>
                    <p>
                      {promotions.length
                        ? "No promotions found. Adjust your filters or search again."
                        : "No promotions have been created yet."}
                    </p>
                  </div>
                ) : (
                  <div>
                    {listItems.map((promo) => (
                      <PromotionItemCard
                        key={promo.id}
                        promotion={promo}
                        actionColWidth={actionColWidth}
                      >
                        {renderItemActions ? renderItemActions(promo) : null}
                      </PromotionItemCard>
                    ))}
                  </div>
                )}
              </div>

              <Pagination
                page={page}
                totalPages={totalPages}
                onPageChange={goToPage}
                ariaLabel="Promotion pages"
                pageSize={pageSize}
                onPageSizeChange={setPageSize}
                resultsLabel={resultsLabel}
              />
            </>
          )}
        </div>
      </div>

      {sortMenuOpen && (
        <div
          className={styles.sortModalBackdrop}
          onClick={() => setSortMenuOpen(false)}
        >
          <div
            className={styles.sortModal}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.sortModalHeader}>Sort By</div>
            <div className={styles.sortModalContent}>
              {[
                { value: "updated", label: "Last Updated" },
                { value: "name", label: "Promotion Name" },
                { value: "status", label: "Status" },
              ].map((opt) => (
                <label className={styles.sortOption} key={opt.value}>
                  <RadioButton
                    name="promo-sort-field"
                    value={opt.value}
                    checked={sortField === opt.value}
                    onChange={() => setSortField(opt.value)}
                  />
                  <span>{opt.label}</span>
                </label>
              ))}
            </div>
            <div className={styles.sortModalDivider} />
            <div className={styles.sortModalHeader}>Order</div>
            <div className={styles.sortModalContent}>
              <label className={styles.sortOption}>
                <RadioButton
                  name="promo-sort-order"
                  value="asc"
                  checked={sortOrder === "asc"}
                  onChange={() => setSortOrder("asc")}
                />
                <span>{orderLabels.asc}</span>
              </label>
              <label className={styles.sortOption}>
                <RadioButton
                  name="promo-sort-order"
                  value="desc"
                  checked={sortOrder === "desc"}
                  onChange={() => setSortOrder("desc")}
                />
                <span>{orderLabels.desc}</span>
              </label>
            </div>
            <button
              className={styles.sortModalClose}
              title="Close the sort options."
              onClick={() => setSortMenuOpen(false)}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

PromotionsCatalog.propTypes = {
  searchPlaceholder: PropTypes.string,
  // eslint-disable-next-line react/forbid-prop-types
  initialFilters: PropTypes.object,
  toolbarActions: PropTypes.node,
  renderItemActions: PropTypes.func,
  actionColWidth: PropTypes.string,
  refreshKey: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

PromotionsCatalog.defaultProps = {
  searchPlaceholder: "Search Promotions",
  initialFilters: {},
  toolbarActions: null,
  renderItemActions: undefined,
  actionColWidth: "220px",
  refreshKey: 0,
};
